import { z } from 'zod';
import {
  FirestoreDataConverter,
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from '@angular/fire/firestore';
import * as Sentry from '@sentry/angular';
import { CURRENCY_USA, SortOrderIndex } from './constants';
import { MembershipSchema } from './membership.codec';

const defaultServices  = [
  'Engine Oil/Filter',
  'Transmission Oil/Filter',
  'Air Filter',
  'Coolant',
  'Hydraulic Oil/Filter',
  'Wiper Blades',
  'Fuel Filter',
  'DEF Filter',
  'Cab Filter',
  'Tires',
  'Differential Oil',
  'Transfer Case Oil',
  'Grease',
] as const;

export const getDefaultServices = () => [...defaultServices];

/**
 * Zod schema for Settings with safe fallbacks
 * Uses .catch() to provide default values on validation failure
 */
export const SettingsSchema = z.object({
  localDistance: z.string().catch('Miles'),
  localCurrency: z.string().catch(CURRENCY_USA),
  sortOrder: z.number().catch(SortOrderIndex.Name),
  colorTheme: z.string().catch('Dark'),
  colorThemes: z.array(z.string()).catch(['Dark', 'Red', 'Green', 'Blue']),
  availServices: z
    .array(z.string())
    .catch(getDefaultServices()),
  paidVersionId: z.string().catch(''),
  useGeoLocation: z.boolean().catch(false),
  dtstamp: z.number().catch(() => Date.now()),
  duid: z.string().catch(''),
  oid: z.string().catch(''),
  pdbConverted: z.boolean().catch(false),
});

/**
 * Settings type inferred from schema
 */
export type Settings = z.infer<typeof SettingsSchema>;

/**
 * Create default Settings object
 */
export function createDefaultSettings(): Settings {
  return {
    localDistance: 'Miles',
    localCurrency: CURRENCY_USA,
    sortOrder: SortOrderIndex.Name,
    colorTheme: 'Dark',
    colorThemes: ['Dark', 'Red', 'Green', 'Blue'],
    availServices: getDefaultServices(),
    paidVersionId: '',
    useGeoLocation: false,
    dtstamp: Date.now(),
    duid: '',
    oid: '',
    pdbConverted: false,
  };
}

/**
 * Profile data schema (subset of ProfileModel stored in Firestore)
 */
export const ProfileDataSchema = z.object({
  firstName: z.string().catch(''),
  lastName: z.string().catch(''),
  businessName: z.string().catch(''),
  shouldReceiveMarketingEmails: z.boolean().catch(true),
}).catch({
  firstName: '',
  lastName: '',
  businessName: '',
  shouldReceiveMarketingEmails: true,
});

export type ProfileData = z.infer<typeof ProfileDataSchema>;

/**
 * Complete UserData schema for users/{uid} document
 */
export const UserDataSchema = z.object({
  settings: SettingsSchema.optional(),
  profile: ProfileDataSchema.optional(),
  membership: MembershipSchema.optional(),
  migration: z.record(z.string(), z.boolean()).optional().catch({}),
});

export type UserData = z.infer<typeof UserDataSchema>;

/**
 * Firestore converter for UserData document
 * Validates settings and profile fields using Zod schemas
 */
export const userDataConverter: FirestoreDataConverter<UserData> = {
  toFirestore: (userData: UserData): DocumentData => {
    return userData;
  },

  fromFirestore: (
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options?: SnapshotOptions
  ): UserData => {
    const data = snapshot.data(options);

    // Use safeParse to validate without throwing
    const result = UserDataSchema.safeParse(data);

    if (!result.success) {
      // Log validation error to Sentry with context
      Sentry.captureException(new Error('UserData validation failed'), {
        level: 'warning',
        extra: {
          docId: snapshot.id,
          docPath: snapshot.ref.path,
          rawData: data,
          zodError: z.treeifyError(result.error),
        },
        tags: {
          dataType: 'userData',
          validationError: 'true',
        },
      });

      // Parse again with .parse() which will use .catch() fallbacks
      console.warn(
        `UserData validation failed for ${snapshot.ref.path}, using fallback values:`,
        z.treeifyError(result.error)
      );
      return UserDataSchema.parse(data);
    }

    return result.data;
  },
};
